import { useState, useEffect, useMemo } from 'react';
import { useSearchParams  } from 'react-router-dom';
import * as EmpDB from '@/lib/employeeDb';
import { formatTRY } from '@/lib/currencyFormat';

type FilterKey = 'all' | 'pending_salary';

function PaymentTypeBadge({ type }: { type: string }) {
  const map: Record<string, { cls: string }> = {
    'Maaş': { cls: 'bg-primary-100 text-primary-700' },
    'Avans': { cls: 'bg-amber-100 text-amber-700' },
    'Prim': { cls: 'bg-emerald-100 text-emerald-700' },
    'Mesai': { cls: 'bg-accent-100 text-accent-700' },
    'Kesinti': { cls: 'bg-red-100 text-red-700' },
    'Diğer': { cls: 'bg-foreground-100 text-foreground-600' },
  };
  const m = map[type] || { cls: 'bg-foreground-100 text-foreground-600' };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${m.cls}`}>
      {type}
    </span>
  );
}

export default function StaffPaymentsPage() {
  const [searchParams] = useSearchParams();
  const src = searchParams.get('src');
  const filterParam = searchParams.get('filter') as FilterKey | null;
  const periodParam = searchParams.get('period');
  const typeParam = searchParams.get('type');

  const [payments, setPayments] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeFilter, setActiveFilter] = useState<FilterKey>(filterParam || 'all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (filterParam) setActiveFilter(filterParam);
  }, [filterParam]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      EmpDB.getAllEmployeePayments(),
      EmpDB.getEmployees(),
    ]).then(([payRes, empRes]) => {
      if (cancelled) return;
      setPayments((payRes.data as any[]) || []);
      setEmployees((empRes.data as any[]) || []);
      setError(null);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { setError('Veri yüklenirken hata oluştu.'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  const employeeMap = useMemo(() => {
    const map: Record<string, any> = {};
    employees.forEach(e => { map[e.id] = e; });
    return map;
  }, [employees]);

  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Istanbul' }));
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Apply filters
  const filtered = useMemo(() => {
    let result = [...payments];

    // Period filter
    if (periodParam === 'this_month') {
      result = result.filter((p: any) => (p.payment_date || '').startsWith(thisMonth));
    }

    // Type filter
    if (typeParam === 'salary') {
      result = result.filter((p: any) => p.payment_type === 'Maaş');
    }

    // Pending salary filter
    if (activeFilter === 'pending_salary') {
      const paidMap: Record<string, number> = {};
      result.forEach((p: any) => {
        if (p.payment_type === 'Maaş' && (p.payment_date || '').startsWith(thisMonth)) {
          paidMap[p.employee_id] = (paidMap[p.employee_id] || 0) + parseFloat(p.amount || '0');
        }
      });

      const pendingEmployees = employees.filter((e: any) => {
        if (e.status !== 'Aktif') return false;
        const salary = parseFloat(e.salary || '0');
        if (salary <= 0) return false;
        const paidThisMonth = paidMap[e.id] || 0;
        return paidThisMonth < salary;
      });

      return pendingEmployees.map((e: any) => {
        const salary = parseFloat(e.salary || '0');
        const paidThisMonth = (() => {
          let sum = 0;
          payments.forEach((p: any) => {
            if (p.employee_id === e.id && p.payment_type === 'Maaş' && (p.payment_date || '').startsWith(thisMonth)) {
              sum += parseFloat(p.amount || '0');
            }
          });
          return sum;
        })();

        return {
          id: `pending-${e.id}`,
          employee_id: e.id,
          payment_type: 'Maaş',
          amount: salary,
          payment_date: null,
          is_pending: true,
          paid_this_month: paidThisMonth,
          remaining: salary - paidThisMonth,
        };
      });
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p: any) => {
        const emp = employeeMap[p.employee_id];
        return (emp?.full_name || '').toLowerCase().includes(q) ||
          (emp?.employee_no || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q);
      });
    }

    // Sort by date descending
    result.sort((a: any, b: any) => (b.payment_date || '').localeCompare(a.payment_date || ''));

    return result;
  }, [payments, employees, employeeMap, periodParam, typeParam, activeFilter, search, thisMonth]);

  // Summary
  const summary = useMemo(() => {
    const monthPayments = payments.filter((p: any) => (p.payment_date || '').startsWith(thisMonth));
    const totalMonth = monthPayments.reduce((s: number, p: any) => s + parseFloat(p.amount || '0'), 0);
    const totalAll = payments.reduce((s: number, p: any) => s + parseFloat(p.amount || '0'), 0);
    const salaryMonth = monthPayments.filter((p: any) => p.payment_type === 'Maaş').reduce((s: number, p: any) => s + parseFloat(p.amount || '0'), 0);
    return { totalMonth, totalAll, salaryMonth, count: payments.length, monthCount: monthPayments.length };
  }, [payments, thisMonth]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Ödemeler yükleniyor...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4"><i className="ri-error-warning-line text-2xl text-red-400"></i></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h2>
        <p className="text-sm text-foreground-400 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Personel Ödemeleri</h2>
          <p className="text-sm text-foreground-400 mt-0.5">
            {filtered.length} kayıt · Bu ay toplam: {formatTRY(summary.totalMonth)}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Bu Ay Toplam</p>
          <p className="text-xl font-bold text-foreground-900 font-heading">{formatTRY(summary.totalMonth)}</p>
          <p className="text-[11px] text-foreground-400 mt-0.5">{summary.monthCount} işlem</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Bu Ay Maaş</p>
          <p className="text-xl font-bold text-primary-600 font-heading">{formatTRY(summary.salaryMonth)}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Toplam Ödeme</p>
          <p className="text-xl font-bold text-foreground-900 font-heading">{formatTRY(summary.totalAll)}</p>
          <p className="text-[11px] text-foreground-400 mt-0.5">{summary.count} işlem</p>
        </div>
        <div className="stat-card">
          <p className="text-xs text-foreground-400 mb-1">Aktif Personel</p>
          <p className="text-xl font-bold text-emerald-600 font-heading">{employees.filter((e: any) => e.status === 'Aktif').length}</p>
        </div>
      </div>

      {/* Search */}
      {activeFilter !== 'pending_salary' && (
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Personel adı, no veya açıklama ile ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
          />
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
        </div>
      )}

      {/* Table / List */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-inbox-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-sm text-foreground-400">Kayıt bulunamadı</p>
          </div>
        ) : activeFilter === 'pending_salary' ? (
          /* Pending Salary List */
          <div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="table-header">Personel No</th>
                    <th className="table-header">Personel</th>
                    <th className="table-header">Pozisyon</th>
                    <th className="table-header">Aylık Maaş</th>
                    <th className="table-header">Bu Ay Ödenen</th>
                    <th className="table-header">Kalan</th>
                    <th className="table-header">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item: any) => {
                    const emp = employeeMap[item.employee_id];
                    return (
                      <tr key={item.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="table-cell font-mono text-xs text-foreground-500">{emp?.employee_no || '—'}</td>
                        <td className="table-cell font-medium text-foreground-800">{emp?.full_name || '—'}</td>
                        <td className="table-cell text-foreground-500">{emp?.position || '—'}</td>
                        <td className="table-cell font-medium">{formatTRY(parseFloat(item.amount || '0'))}</td>
                        <td className="table-cell text-emerald-600 font-medium">{formatTRY(item.paid_this_month)}</td>
                        <td className="table-cell text-red-500 font-semibold">{formatTRY(item.remaining)}</td>
                        <td className="table-cell py-2">
                          <span className="bg-amber-100 text-amber-700 text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                            {item.remaining > 0 ? 'Eksik Ödendi' : 'Bekliyor'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <div className="lg:hidden p-3 space-y-2">
              {filtered.map((item: any) => {
                const emp = employeeMap[item.employee_id];
                return (
                  <div key={item.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-foreground-400">{emp?.employee_no || '—'}</span>
                      <span className="bg-amber-100 text-amber-700 text-[10px] px-2 py-0.5 rounded-full font-medium">
                        {item.remaining > 0 ? 'Eksik' : 'Bekliyor'}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground-800">{emp?.full_name || '—'}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">{emp?.position || '—'}</span>
                      <span className="font-medium text-foreground-700">Maaş: {formatTRY(parseFloat(item.amount || '0'))}</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-background-100 text-xs">
                      <span className="text-foreground-400">Ödenen: <span className="text-emerald-600 font-medium">{formatTRY(item.paid_this_month)}</span></span>
                      <span className="text-red-500 font-semibold">Kalan: {formatTRY(item.remaining)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Regular Payments List */
          <div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/50">
                    <th className="table-header">Tarih</th>
                    <th className="table-header">Personel</th>
                    <th className="table-header">Personel No</th>
                    <th className="table-header">Tür</th>
                    <th className="table-header">Tutar</th>
                    <th className="table-header">Ödeme Yöntemi</th>
                    <th className="table-header">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p: any) => {
                    const emp = employeeMap[p.employee_id];
                    if (p.is_pending) return null; // pending items have no real payment record
                    return (
                      <tr key={p.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="table-cell text-sm">
                          {p.payment_date ? new Date(p.payment_date).toLocaleDateString('tr-TR') : '—'}
                        </td>
                        <td className="table-cell font-medium text-foreground-800">{emp?.full_name || '—'}</td>
                        <td className="table-cell font-mono text-xs text-foreground-500">{emp?.employee_no || '—'}</td>
                        <td className="table-cell py-2"><PaymentTypeBadge type={p.payment_type || 'Diğer'} /></td>
                        <td className="table-cell font-semibold text-foreground-800">{formatTRY(parseFloat(p.amount || '0'))}</td>
                        <td className="table-cell text-foreground-500 text-sm">{p.payment_method || '—'}</td>
                        <td className="table-cell text-foreground-400 text-xs max-w-[200px] truncate">{p.description || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile */}
            <div className="lg:hidden p-3 space-y-2">
              {filtered.map((p: any) => {
                if (p.is_pending) return null;
                const emp = employeeMap[p.employee_id];
                return (
                  <div key={p.id} className="bg-background-50 border border-background-200/70 rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <PaymentTypeBadge type={p.payment_type || 'Diğer'} />
                      <span className="text-xs text-foreground-400">
                        {p.payment_date ? new Date(p.payment_date).toLocaleDateString('tr-TR') : '—'}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-foreground-800">{emp?.full_name || '—'}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400 font-mono">{emp?.employee_no || '—'}</span>
                      <span className="font-semibold text-foreground-800">{formatTRY(parseFloat(p.amount || '0'))}</span>
                    </div>
                    {p.description && <p className="text-[11px] text-foreground-400">{p.description}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}